import { FISHIQ_SPECIES_IMAGES } from '../data/fishIQSpeciesImages';
import type { FishIQSpeciesImage } from '../types';

const clean = (value: unknown) => String(value ?? '').trim();
const key = (value: unknown) => clean(value).toLowerCase();

const isVerifiedForDisplay = (image: FishIQSpeciesImage) =>
  image.verified === true &&
  image.speciesVerified === true &&
  image.rightsVerified === true;

const isVerifiedForScanner = (image: FishIQSpeciesImage) =>
  isVerifiedForDisplay(image) && image.scannerEligible === true;

const sortReferences = (images: FishIQSpeciesImage[]) => {
  const angleOrder: Record<string, number> = {
    side: 0,
    caught: 1,
    juvenile: 2,
    top: 3,
    alternate: 4,
  };

  return [...images].sort((a, b) => {
    const angleDelta = (angleOrder[a.angle] ?? 99) - (angleOrder[b.angle] ?? 99);
    if (angleDelta !== 0) return angleDelta;
    return a.id.localeCompare(b.id);
  });
};

export function getFishIQSpeciesImages(
  canonicalSpeciesId?: string | null,
  scientificName?: string | null,
): FishIQSpeciesImage[] {
  const idKey = key(canonicalSpeciesId);
  const scientificKey = key(scientificName);

  if (!idKey && !scientificKey) return [];

  return sortReferences(
    FISHIQ_SPECIES_IMAGES.filter((image) => {
      if (idKey && key(image.canonicalSpeciesId) === idKey) return true;
      if (scientificKey && key(image.scientificName) === scientificKey) return true;
      return false;
    }),
  );
}

export function getFishIQDisplayImages(
  canonicalSpeciesId?: string | null,
  scientificName?: string | null,
): FishIQSpeciesImage[] {
  return getFishIQSpeciesImages(canonicalSpeciesId, scientificName).filter(isVerifiedForDisplay);
}

export function getFishIQDisplayImage(
  canonicalSpeciesId?: string | null,
  scientificName?: string | null,
): FishIQSpeciesImage | null {
  return getFishIQDisplayImages(canonicalSpeciesId, scientificName)[0] ?? null;
}

export function getFishIQScannerReferences(
  canonicalSpeciesId?: string | null,
  scientificName?: string | null,
): FishIQSpeciesImage[] {
  return getFishIQSpeciesImages(canonicalSpeciesId, scientificName).filter(isVerifiedForScanner);
}

export function getFishIQSpeciesImageCredits(
  canonicalSpeciesId?: string | null,
  scientificName?: string | null,
): string[] {
  const seen = new Set<string>();
  const credits: string[] = [];

  for (const image of getFishIQDisplayImages(canonicalSpeciesId, scientificName)) {
    const credit = clean(image.creditText);
    const normalized = key(credit);
    if (!credit || seen.has(normalized)) continue;
    seen.add(normalized);
    credits.push(credit);
  }

  return credits;
}

export function resolveFishIQSpeciesIdentity(species: any): {
  canonicalSpeciesId: string;
  scientificName: string;
} {
  const idCandidates = [
    species?.canonicalSpecies?.canonicalId,
    species?.canonicalSpeciesId,
    species?.speciesId,
    species?.id,
    species?.slug,
    species?.key,
    species?.speciesKey,
    ...(Array.isArray(species?.canonicalSpecies?.legacyIds) ? species.canonicalSpecies.legacyIds : []),
    ...(Array.isArray(species?.legacyIds) ? species.legacyIds : []),
  ]
    .map(clean)
    .filter(Boolean);

  const scientificCandidates = [
    species?.scientificName,
    species?.taxonomy?.scientificName,
    species?.canonicalSpecies?.scientificName,
    species?.canonicalSpecies?.normalizedScientificName,
  ]
    .map(clean)
    .filter(Boolean);

  // Prefer an identity that is actually represented in the audited image
  // manifest. This matters when Roddo's canonical metadata is more normalized
  // than the storage/image manifest id (for example Spanish Mackerel after the
  // Scomberomorus maculatus / S. commerson split).
  const matchedById = idCandidates.find((candidate) =>
    FISHIQ_SPECIES_IMAGES.some((image) => key(image.canonicalSpeciesId) === key(candidate)),
  );

  const matchedByScientific = scientificCandidates.find((candidate) =>
    FISHIQ_SPECIES_IMAGES.some((image) => key(image.scientificName) === key(candidate)),
  );

  return {
    canonicalSpeciesId: matchedById || idCandidates[0] || '',
    scientificName: matchedByScientific || scientificCandidates[0] || '',
  };
}
